import React from "react";
import { motion } from "framer-motion";

const SECTIONS = [
  {
    id: "freelancing",
    label: "Le freelancing",
    description: "Le produit, c'est vous.",
    image: "/images/card-freelance-homepage-v5.webp",
    eyebrowColor: "#000000",
    footnote: "Chaque mission dépend de votre temps, de votre disponibilité et de votre présence.\nLorsque vous vous arrêtez, vos revenus s'arrêtent aussi."
  },
  {
    id: "opportunity",
    label: "Votre système",
    description: "Vous n'êtes plus le produit.",
    image: "/images/tangig-systeme-hub-desktop.webp",
    mobileImage: "/images/tangig-systeme-hub-mobile.webp",
    eyebrowColor: "#000000",
    footnote: "Votre expertise devient un système réutilisable. Publiez-le une fois, améliorez-le au fil du temps\net distribuez-le sans repartir de zéro à chaque nouvelle mission."
  },
  {
    id: "result",
    label: "Le résultat",
    description: "Licence transférable · Système actif",
    image: "/images/card-resultat-homepage-v8.webp",
    eyebrowColor: "#000000",
    footnote: "Votre nouvelle liberté. Votre nouvelle économie. Vous ne vendez plus uniquement votre temps."
  }
];

const CSS = `
  .tg-carousel-section {
    background: #F5F5F7;
    padding: 120px 0;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .tg-carousel-header {
    text-align: center;
    margin-bottom: 64px;
    padding: 0 24px;
  }

  .tg-carousel-title {
    font-size: clamp(2.4rem, 5vw, 4rem);
    font-weight: 600;
    line-height: 1.1;
    letter-spacing: -0.022em;
    color: #1D1D1F;
    margin: 0;
  }

  .tg-carousel-subtitle {
    font-size: 19px;
    line-height: 1.5;
    font-weight: 600;
    color: #6E6E73;
    max-width: 560px;
    margin: 24px auto 0;
  }

  .tg-carousel-dots {
    display: flex;
    gap: 12px;
    margin-bottom: 40px;
    justify-content: center;
  }

  .tg-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #D2D2D7;
    transition: all 0.4s ease;
    cursor: pointer;
  }

  .tg-dot--active {
    background: #86868B;
    transform: scale(1.2);
  }

  .tg-dot:hover {
    background: #86868B;
  }

  .tg-carousel-viewport {
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    display: flex;
    gap: 28px;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
    padding: 0 calc((100vw - min(1000px, 90vw)) / 2);
    scrollbar-width: none;
  }

  .tg-carousel-viewport::-webkit-scrollbar {
    display: none;
  }

  .tg-carousel-item {
    scroll-snap-align: center;
    flex: 0 0 min(1000px, 90vw);
    box-sizing: border-box;
  }

  /* La card "opportunity" a désormais la même largeur que les 2 autres
     (le visuel hub paysage remplit tout le cadre). */
  .tg-carousel-item--opportunity {
    flex: 0 0 min(1000px, 90vw);
  }

  /* =========================================================
   APPLE CARD — pure white, image-only
   ========================================================= */

  .apple-card {
    position: relative;
    overflow: hidden;
    border-radius: 28px;
    aspect-ratio: 43 / 27;
    width: 100%;
    background: #FFFFFF;
    transition: transform 0.8s cubic-bezier(0.2, 0, 0, 1);
  }

  /* Pas d'ombre au repos : à plat comme sur Apple. Avec 3 cards espacées de 28px,
     une ombre permanente sur chacune se chevauche dans les gaps et forme une traînée
     grisâtre continue sous toute la rangée, très voyante pendant le scroll horizontal.
     L'élévation n'apparaît qu'à l'interaction (survol), jamais au repos. */
  .apple-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.10);
  }

  /* Card 1 (Freelancing): pure white, studio-lit portrait. */
  .apple-card--freelancing {
    background: #FFFFFF;
  }


  /* =========================================================
   EYEBROW LABEL + SUBTITLE — on top of the image, left, mid-height, no pill
   ========================================================= */

  .poster-card-heading {
    position: absolute;
    top: 50%;
    left: 8%;
    transform: translateY(-50%);
    z-index: 10;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
    max-width: 78%;
  }

  .poster-eyebrow-chip {
    font-size: 37px;
    font-weight: 700;
    letter-spacing: 0.01em;
    text-shadow: 0 2px 14px rgba(0,0,0,0.18);
  }

  .poster-card-subtitle {
    margin: 12px 0 0;
    font-size: 17px;
    font-weight: 600;
    line-height: 1.4;
    letter-spacing: -0.01em;
    max-width: 320px;
    text-shadow: 0 2px 14px rgba(0,0,0,0.18);
  }

  /* Card 2 (desktop) : titre + sous-titre en haut à gauche, dans l'espace
     blanc au-dessus/à gauche de la composition hub. */
  .apple-card--opportunity .poster-card-heading {
    top: 32px;
    left: 8%;
    right: auto;
    max-width: 78%;
    transform: none;
    align-items: flex-start;
    text-align: left;
  }

  /* =========================================================
   IMAGE ZONE
   ========================================================= */

  .image-zone {
    position: absolute;
    inset: 0;
    display: flex;
  }

  /* <picture> ne crée pas de boîte : l'<img> reste l'enfant flex direct de
     .image-zone, donc toutes les règles .image-zone img continuent de s'appliquer. */
  .image-zone picture {
    display: contents;
  }

  .image-zone img {
    display: block;
  }

  /* Card 1 (Freelancing): full-bleed cover photo, woman framed on the right.
     Photo détourée avec ~7-8% de marge transparente au-dessus des cheveux ;
     avec ce ratio de card, object-fit:cover ne recadre que horizontalement,
     jamais verticalement, donc scale() est nécessaire pour sortir cette
     marge du cadre visible (calculé sur la marge mesurée de cette photo). */
  .apple-card--freelancing .image-zone img {
    height: 100%;
    width: 100%;
    object-fit: cover;
    object-position: 30% center;
    transform: scale(1.16);
    filter: brightness(0.86) contrast(1.08);
  }

  /* Card 2 (Opportunity): visuel "hub TANGIG" sur fond clair. La card passe en
     blanc et l'image (padée au ratio 43/27) remplit tout le cadre. Une ombre
     subtile révèle l'arrondi des coins (sinon le blanc sur le fond gris clair du
     carrousel efface visuellement les coins). Le titre "Votre système" (bleu)
     reste posé en haut, centré, dans l'espace blanc au-dessus de la composition. */
  .apple-card--opportunity {
    background: #FFFFFF;
    aspect-ratio: 43 / 27;
    box-shadow: 0 12px 30px rgba(0,0,0,0.08);
  }

  .apple-card--opportunity .image-zone {
    align-items: stretch;
    justify-content: center;
  }

  .apple-card--opportunity .image-zone img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    filter: none;
    transition: transform 0.8s cubic-bezier(0.2, 0, 0, 1);
  }

  /* Card 3 (Result): même traitement clair que la card 1. */
  .apple-card--result {
    background: #050505;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }

  /* Voile studio léger, uniquement sur le visage (pas toute la card) :
     assombrit doucement les hautes lumières du visage pour donner du
     modelé, comme un éclairage studio Apple plutôt qu'un aplat plat.
     mix-blend-mode: multiply + dégradé qui remonte vers le blanc pur en
     sortant du visage : sur le fond blanc de la photo, blanc x blanc =
     blanc (invisible), donc aucun halo gris ne déborde sur le fond. */
  .apple-card--result::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    mix-blend-mode: multiply;
    background: radial-gradient(ellipse 30% 45% at 62% 40%, rgba(0, 0, 0, 0.2) 0%, rgba(255, 255, 255, 1) 55%);
  }

  .apple-card--result .image-zone img {
    height: 100%;
    width: 100%;
    object-fit: cover;
    object-position: center center;
    transform: scale(1.04);
    filter: brightness(1) contrast(1.05);
  }

  /* =========================================================
   RESPONSIVE
   ========================================================= */

  @media (max-width: 768px) {
    .tg-carousel-section {
      padding: 72px 0;
    }
    .tg-carousel-header {
      margin-bottom: 28px;
      padding: 0 22px;
    }
    .tg-carousel-title {
      font-size: clamp(1.9rem, 7.5vw, 2.4rem);
    }
    .tg-carousel-subtitle {
      font-size: 15px;
      margin-top: 14px;
    }
    .tg-carousel-dots {
      margin-bottom: 24px;
    }

    /* Scroll horizontal conservé, mais cards en PORTRAIT 2:3 façon Apple mobile :
       ~78vw de large, la card voisine dépasse légèrement (peek). */
    .tg-carousel-viewport {
      gap: 16px;
      padding: 0 11vw;
    }
    .tg-carousel-item,
    .tg-carousel-item--opportunity {
      flex: 0 0 78vw;
    }
    .apple-card,
    .apple-card--opportunity {
      aspect-ratio: 2 / 3;
      border-radius: 22px;
    }

    /* Images dézoomées sur mobile (le crop desktop est trop serré en portrait). */
    .apple-card--freelancing .image-zone img {
      transform: scale(1.03);
      object-position: 42% center;
    }
    /* Card résultat en mode "texte en haut / image en dessous" : la photo
       (homme sur fond blanc, sujet en haut à droite) rend mal en plein cadre
       recadré. On passe la card en fond blanc, on désactive le voile studio,
       et on cantonne l'image détourée dans la moitié basse, centrée. */
    .apple-card--result {
      background: #FFFFFF;
      box-shadow: 0 12px 24px rgba(0,0,0,0.06);
    }
    .apple-card--result::before {
      display: none;
    }
    /* Texte en haut, homme EN GRAND dans la partie basse : l'image remplit
       la zone basse en cover, cadrée sur l'homme (côté droit de la source).
       Son fond blanc = fond de la card, donc pas de couture visible entre
       le bloc texte (haut) et la photo (bas). */
    .apple-card--result .image-zone {
      top: 30%;
    }
    .apple-card--result .image-zone img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: 60% center;
      transform: none;
      filter: none;
    }
    /* Nouveau visuel "hub TANGIG" sur fond clair : la card passe en blanc pour
       que l'image (padée au ratio 2:3) se fonde parfaitement et remplisse tout
       le cadre. Le titre "Votre système" (bleu) se pose dans l'espace blanc du
       haut, au-dessus de la composition. */
    .apple-card--opportunity {
      background: #FFFFFF;
      /* Ombre subtile (comme la card résultat) : sans elle, l'image blanche
         sur le fond gris clair du carrousel efface visuellement l'arrondi des
         coins et la card paraît rectangulaire. */
      box-shadow: 0 12px 24px rgba(0,0,0,0.06);
    }
    .apple-card--opportunity .image-zone {
      top: 0;
      align-items: stretch;
    }
    .apple-card--opportunity .image-zone img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
      /* Légère descente pour dégager le titre en haut (le très bas de la
         main est rogné, sans conséquence). */
      transform: translateY(4%);
    }

    /* Label + sous-titre : haut-gauche sur toutes les cards, plus compacts. */
    .poster-card-heading,
    .apple-card--opportunity .poster-card-heading {
      top: 22px;
      left: 22px;
      right: 22px;
      max-width: calc(100% - 44px);
      transform: none;
      align-items: flex-start;
      text-align: left;
    }
    /* Carte "système" : titre en haut, pleine largeur, centré — "Votre
       système" tient sur une seule ligne et se pose au-dessus du visuel. */
    .apple-card--opportunity .poster-card-heading {
      top: 5%;
      left: 22px;
      right: 22px;
      width: auto;
      max-width: calc(100% - 44px);
      align-items: center;
      text-align: center;
    }
    /* Le sous-titre "Vous n'êtes plus le produit" n'apparaît que sur desktop. */
    .apple-card--opportunity .poster-card-subtitle {
      display: none;
    }
    .poster-eyebrow-chip {
      font-size: 21px;
    }
    .poster-card-subtitle {
      font-size: 13px;
      margin-top: 6px;
      max-width: 100%;
    }
    .tg-card-footnote {
      font-size: 13px;
      margin-top: 14px;
      line-height: 1.45;
    }
  }

  .tg-card-footnote {
    margin: 20px 4px 0;
    font-size: 15px;
    line-height: 1.5;
    font-weight: 700;
    color: #6E6E73;
    max-width: none;
    /* Respecte les sauts de ligne (\n) insérés dans les footnotes des cards
       pour éviter des lignes trop larges. La card 3 n'a pas de \n → inchangée. */
    white-space: pre-line;
  }

`;

export default function SellersSection() {
  const [activeIdx, setActiveIdx] = React.useState(0);
  const viewportRef = React.useRef(null);

  const handleScroll = () => {
    if (!viewportRef.current) return;
    const scrollLeft = viewportRef.current.scrollLeft;
    const width = viewportRef.current.offsetWidth;
    const newIdx = Math.round(scrollLeft / width);
    if (newIdx !== activeIdx) setActiveIdx(newIdx);
  };

  const scrollTo = (idx) => {
    if (!viewportRef.current) return;
    const width = viewportRef.current.offsetWidth;
    viewportRef.current.scrollTo({
      left: idx * width,
      behavior: 'smooth'
    });
  };

  return (
    <>
      <style>{CSS}</style>
      <section className="tg-carousel-section">
        <div className="tg-carousel-header">
           <motion.h2
            className="tg-carousel-title"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Le travail évolue.
          </motion.h2>
          <motion.p
            className="tg-carousel-subtitle"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
          >
            Pendant des années, nous avons vendu notre temps.<br />
            Il est peut-être temps de construire quelque chose qui travaille pour nous.
          </motion.p>
        </div>

        <div className="tg-carousel-dots">
          {SECTIONS.map((_, i) => (
            <div
              key={i}
              className={`tg-dot ${i === activeIdx ? 'tg-dot--active' : ''}`}
              onClick={() => scrollTo(i)}
            />
          ))}
        </div>

        <div
          className="tg-carousel-viewport"
          ref={viewportRef}
          onScroll={handleScroll}
        >
          {SECTIONS.map((section) => (
            <div key={section.id} className={`tg-carousel-item tg-carousel-item--${section.id}`}>
              <div className={`apple-card apple-card--${section.id}`}>
                <div className="poster-card-heading">
                  <span className="poster-eyebrow-chip" style={{ color: section.eyebrowColor }}>
                    {section.label}
                  </span>
                  {section.description && (
                    <p className="poster-card-subtitle" style={{ color: section.eyebrowColor }}>
                      {section.description}
                    </p>
                  )}
                </div>

                <div className="image-zone">
                  {section.mobileImage ? (
                    <picture>
                      {/* Version verticale 2:3 dédiée mobile ; le desktop garde
                          le visuel paysage. */}
                      <source media="(max-width: 768px)" srcSet={section.mobileImage} />
                      <img src={section.image} alt={section.label} />
                    </picture>
                  ) : (
                    <img src={section.image} alt={section.label} />
                  )}
                </div>
              </div>

              {section.footnote && (
                <p className="tg-card-footnote">{section.footnote}</p>
              )}
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
